import { useCallback, useEffect, useState } from "react";
import { BarChart3, ChevronDown, Loader2, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { WiredMinhasEstatisticas, wiredService } from "../../services/wired";

const INTERVALO_MS = 30_000;

function formatarValor(valor: string): string {
  try {
    return BigInt(valor).toLocaleString("pt-BR");
  } catch {
    return valor;
  }
}

export function Estatisticas() {
  const [dados, setDados] = useState<WiredMinhasEstatisticas | null>(null);
  const [erro, setErro] = useState("");
  const [atualizando, setAtualizando] = useState(false);
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set());

  const alternar = (nome: string) =>
    setRecolhidas((atual) => {
      const novo = new Set(atual);
      if (!novo.delete(nome)) novo.add(nome);
      return novo;
    });

  const carregar = useCallback(async () => {
    try {
      setDados(await wiredService.obterMinhasEstatisticas());
      setErro("");
    } catch (error: any) {
      setErro(error.response?.data?.message || "Não foi possível carregar as estatísticas.");
    }
  }, []);

  const recarregar = async () => {
    setAtualizando(true);
    try {
      await carregar();
    } finally {
      setAtualizando(false);
    }
  };

  useEffect(() => {
    carregar();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") carregar();
    }, INTERVALO_MS);
    return () => window.clearInterval(timer);
  }, [carregar]);

  if (!dados) {
    return erro ? (
      <p className="text-sm text-red-600">{erro}</p>
    ) : (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={recarregar}
          disabled={atualizando}
          title="Atualizar estatísticas"
          aria-label="Atualizar estatísticas"
        >
          <RefreshCw className={`h-4 w-4 ${atualizando ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {erro && <p className="text-sm text-red-600">{erro}</p>}

      {dados.categorias.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma estatística disponível.</p>
      ) : (
        dados.categorias.map((categoria) => {
          const fechada = recolhidas.has(categoria.nome);
          return (
            <section key={categoria.nome} className="space-y-1">
              <button
                type="button"
                onClick={() => alternar(categoria.nome)}
                aria-expanded={!fechada}
                className="flex w-full items-center gap-2 text-sm font-semibold uppercase text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${fechada ? "-rotate-90" : ""}`} />
                <BarChart3 className="h-4 w-4" />
                {categoria.nome}
                <span className="font-normal normal-case">({categoria.itens.length})</span>
              </button>
              {!fechada && (
                <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                  {categoria.itens.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-1">
                      <span className="text-sm leading-tight">{item.rotulo}</span>
                      <span className="font-mono text-sm font-semibold">{formatarValor(item.valor)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
